import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import {
  IBenefitsContext,
  IBenefitsDetails,
  IBenefitsList,
  IPartnerLaboratoriesList,
} from '../benefits-types'
import httpClient from '@/services/httpClient'
import { toast } from '@conexasaude/conexa-hero'
import { useModalContext } from '@/components/Modal/contexts/ModalContext'
import { FunctionComponentChildren } from '@/@types/react'

const BenefitsContext = createContext<IBenefitsContext>(null)

export const BenefitsProvider: FunctionComponentChildren = ({ children }) => {
  const [benefitsList, setBenefitsList] = useState<IBenefitsList[]>(null)
  const [loading, setLoading] = useState(false)
  const { closeModal } = useModalContext()
  const [healthInsuranceSummary, setHealthInsuranceSummary] =
    useState<IBenefitsDetails>(null)
  const [partnerLaboratoriesList, setPartnerLaboratoriesList] =
    useState<IPartnerLaboratoriesList[]>(null)

  const getBenefitsList = useCallback(async () => {
    try {
      setLoading(true)
      const response = await httpClient.get(
        `/integration/app-paciente/beneficio/listar`,
      )

      setBenefitsList(response.data.object)
    } catch (err) {
      const { msg } = err?.response?.data
      toast({
        message: msg,
        variant: 'error',
        autoCloseDuration: 5000,
        position: 'top-right',
      })
    } finally {
      setLoading(false)
    }
  }, [])

  const handleAcceptTerm = useCallback(
    async idBenefits => {
      try {
        await httpClient.post(
          '/integration/app-paciente/beneficio/aceitar-termo',
          {
            idTipoBeneficio: idBenefits,
          },
        )

        closeModal()
        getBenefitsList()
      } catch (err) {
        const { msg } = err?.response?.data
        toast({
          message: msg,
          variant: 'error',
          autoCloseDuration: 5000,
          position: 'top-right',
        })
      }
    },
    [closeModal, getBenefitsList],
  )

  const getHealthInsuranceSummary = useCallback(async idBenefits => {
    try {
      setLoading(true)
      const response = await httpClient.get(
        `/integration/app-paciente/beneficio/listar/${idBenefits}`,
      )

      const [benefit] = response.data.object

      setHealthInsuranceSummary(benefit.detalhes)
    } catch (err) {
      const { msg } = err?.response?.data
      toast({
        message: msg,
        variant: 'error',
        autoCloseDuration: 5000,
        position: 'top-right',
      })
    } finally {
      setLoading(false)
    }
  }, [])

  const getPartnerLaboratories = useCallback(async () => {
    try {
      setLoading(true)
      const response = await httpClient.get(
        `/integration/app-paciente/beneficio/listar/laboratorio-parceiro`,
      )

      setPartnerLaboratoriesList(response.data.object)
    } catch (err) {
      const { msg } = err?.response?.data
      toast({
        message: msg,
        variant: 'error',
        autoCloseDuration: 5000,
        position: 'top-right',
      })
    } finally {
      setLoading(false)
    }
  }, [])

  const values = useMemo(
    () => ({
      benefitsList,
      loading,
      healthInsuranceSummary,
      partnerLaboratoriesList,
      handleAcceptTerm,
      getBenefitsList,
      getHealthInsuranceSummary,
      getPartnerLaboratories,
    }),
    [
      benefitsList,
      loading,
      healthInsuranceSummary,
      partnerLaboratoriesList,
      handleAcceptTerm,
      getBenefitsList,
      getHealthInsuranceSummary,
      getPartnerLaboratories,
    ],
  )

  return (
    <BenefitsContext.Provider value={values}>
      {children}
    </BenefitsContext.Provider>
  )
}

export const useBenefitsContext = (): IBenefitsContext => {
  const context = useContext(BenefitsContext)

  if (!context) {
    throw new Error('useBenefitsContext must be used within a BenefitsProvider')
  }

  return context
}
